import { computed, ref, watch, toValue } from 'vue'
import type { MaybeRefOrGetter } from 'vue'

import { useRemoteSelect } from './useRemoteSelect'
import type { EntityId, EntityListParams } from './types'
import { compactIds } from './ids'
import { provideListErrorOf } from './listErrorState'
import { provideListTotal } from './listTotalState'
import { useLoadingState } from './loadingState'

type UseListFn<TParams, TItem> = Parameters<
  typeof useRemoteSelect<TItem, TParams>
>[0]

type SelectValue = string | number | null | undefined | (string | number)[]

function isEmptySelection(value: SelectValue): boolean {
  return Array.isArray(value)
    ? value.length === 0
    : value == null || value === ''
}

/**
 * Everything an `<Entity>Select.vue` needs, so that the component is a template and a
 * `mapOption` and nothing else.
 *
 * What differs per entity — which list hook, how a row is labelled, what narrows it —
 * are the arguments; everything else lives here.
 *
 * The watcher is the non-obvious part. Clearing the field has to reset the search too,
 * otherwise the dropdown reopens still filtered by the last query and the user is shown
 * one option where the list has hundreds.
 */
export function useEntitySelect<
  TItem,
  // Both are inferred from the list hook, so a call site passes no type arguments at all:
  // naming only `TItem` leaves this one at its default and the filter unchecked.
  TParams extends EntityListParams = EntityListParams,
  /** Exact, for the reason spelled out on `useEntityTable`. */
  TFilter extends NonNullable<TParams['filter']> &
    Record<
      Exclude<keyof TFilter, keyof NonNullable<TParams['filter']>>,
      never
    > = NonNullable<TParams['filter']>
>(
  useList: UseListFn<TParams, TItem>,
  options: {
    /**
     * One option out of one record — the whole of what differs between two entity selects.
     * The value is an `EntityId` and not `any`, so the type survives into the options the
     * field renders.
     */
    mapOption: (item: TItem) => {
      label: string
      value: EntityId
      status?: boolean | null
      statusText?: string
    }
    /** The field's current value — the search resets when it is cleared. */
    modelValue?: MaybeRefOrGetter<SelectValue>
    /** Ids to keep listed even when the current search would not return them. */
    selected?: MaybeRefOrGetter<(string | number)[] | undefined>
    filter?: MaybeRefOrGetter<TFilter | undefined>
    sort?: Record<string, 'asc' | 'desc'>
    limit?: number
    enabled?: MaybeRefOrGetter<boolean>
    /**
     * Ask for the first page only once the dropdown has been opened, rather than on mount.
     * **On by default** — pass `false` for a field that must fill itself unasked.
     *
     * A list nobody has opened is a request nobody asked for, and a panel or form can mount
     * five of these at once. A field that already holds a value fetches anyway — the value
     * is an id, and the list is what turns it into a name.
     *
     * **Bind the returned `onVisibleChange` to the select**, or the field never learns it
     * was opened and, holding no value, never fetches at all. A component that gates the
     * list by its own means passes `false` here instead.
     */
    lazy?: MaybeRefOrGetter<boolean | undefined>
  }
) {
  // An enclosing modal that is still fetching its record holds the list back: the select's
  // value is seeded from that record, so asking now costs one request without `selected`
  // and a second the moment the value lands. Composed with the caller's own `enabled`
  // rather than replacing it — both conditions have to hold.
  const containerLoading = useLoadingState()

  const opened = ref(false)

  function onVisibleChange(open: boolean) {
    if (open) opened.value = true
  }

  const lazy = computed(() => toValue(options.lazy) ?? true)

  const wanted = computed(
    () =>
      !lazy.value ||
      opened.value ||
      !isEmptySelection(toValue(options.modelValue))
  )

  /**
   * The list is worth having: the caller allows it and something has asked for it. Kept
   * apart from the `enabled` below because a container that is still loading is a reason
   * to *wait* for the list, not a reason to stop wanting it — the field says "loading"
   * through that wait rather than "nothing found".
   */
  const wantsList = computed(
    () => (toValue(options.enabled) ?? true) && wanted.value
  )

  // Compacted, not just length-checked: a form seeded from a record whose payload
  // spells the id field differently holds `'undefined'` strings, which are truthy and
  // would ride into the query as `selected[]=undefined`.
  const selectedIds = computed(() => compactIds(toValue(options.selected)))

  const remote = useRemoteSelect<TItem, TParams>(useList, {
    mapOption: options.mapOption,
    limit: options.limit,
    enabled: computed(() => wantsList.value && !containerLoading.value),
    // The endpoint appends these to every page on top of `limit` and counts the total
    // without them, so `useRemoteSelect` cannot tell how long the list is without it.
    pinnedCount: computed(() => selectedIds.value.length),
    buildParams: ({ query, page, limit }) => {
      const selected = selectedIds.value
      const filter = toValue(options.filter)

      // Key by key, for the reason `useEntityTable` builds its params that way: a
      // literal with a spread in it is not checked for keys it should not have.
      return {
        limit,
        page,
        query,
        selected: selected.length ? selected : undefined,
        filter,
        sort: options.sort
      } satisfies EntityListParams as TParams
    }
  })

  /**
   * A dropdown that could not be filled says so. Without this it reads "No data", which
   * is what an empty catalog looks like — the publisher sees a field with nothing to
   * pick and no reason why.
   */
  provideListErrorOf({
    isError: remote.isError,
    error: remote.error,
    refetch: remote.retry
  })

  /**
   * How long the list is, for the field to say what it is showing a page of — already
   * corrected for the pinned ids by `useRemoteSelect`.
   */
  provideListTotal(() => remote.total.value)

  /**
   * Narrowing the list makes it a different list, so the page counter goes back to the
   * start — otherwise a field scrolled to page three asks the narrowed endpoint for its
   * page three, and `useRemoteSelect` appends the answer to rows the old filter returned.
   *
   * Compared by value: a panel builds its `filter` object afresh on every render, so
   * watching the object itself would reset the paging on every keystroke elsewhere.
   */
  watch(
    () => JSON.stringify(toValue(options.filter) ?? null),
    () => remote.resetPaging(),
    // Synchronously, or the query gets there first: the request is keyed on a params
    // object that already holds the new filter beside the old page, and the narrowed list
    // is asked for a page three that only the wide one had.
    { flush: 'sync' }
  )

  /**
   * The list says it is loading from the moment it is wanted, not from the moment the
   * request leaves.
   *
   * A lazy list is idle until its dropdown opens, so between the click and the query
   * starting there is a frame where nothing is loading and nothing has arrived — and
   * `RemoteSelect` fills an empty, not-loading dropdown with "Nothing found".
   *
   * Read off `hasLoaded`, a fact about the query, and **not** off a flag latched the first
   * time `isLoading` turned true: an eager field's request is already in flight when this
   * hook runs, so such a flag never latches and the field spins "Loading…" for good over a
   * list that arrived long ago.
   */
  const isLoading = computed(
    () => remote.isLoading.value || (wantsList.value && !remote.hasLoaded.value)
  )

  if (options.modelValue !== undefined) {
    watch(
      () => toValue(options.modelValue),
      (value) => {
        if (isEmptySelection(value)) remote.onSearch('')
      },
      { immediate: true }
    )
  }

  return { ...remote, isLoading, onVisibleChange }
}
